import React, {useState, useEffect} from "react";
import {useParams, useNavigate, useLocation} from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Breadcrumbs,
  Link,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {Spinner, CustomizedSnackbars} from "../../../../components";
import NumberFormat from "../../../../helpers/functions/numberFormat";
import {formatDate} from "../../utils/formatters";

// Import hooks
import {useStockOpnameData} from "../../hooks/useStockOpnameData";

function StockOpnameDetail() {
  const {id} = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const stockOpnameFromState = location.state?.stockOpname;

  // Hooks
  const {isSpinner, fetchStockOpnameDetail, grandTotalStockOpname} =
    useStockOpnameData();

  // Local state
  const [isLoading, setIsLoading] = useState(true);
  const [detailStockOpname, setDetailStockOpname] = useState(null);
  const [detailStockOpnameInfo, setDetailStockOpnameInfo] = useState([]);
  const [pageDetailSO, setPageDetailSO] = useState(0);
  const [rowsPerPageDetailSO, setRowsPerPageDetailSO] = useState(10);
  const [totalRecordDetailSO, setTotalRecordDetailSO] = useState(0);

  // Alert state
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");

  // Load detail data
  useEffect(() => {
    const loadDetailData = async () => {
      setIsLoading(true);
      try {
        const stockOpnameId = stockOpnameFromState?.id || id;

        const detail = await fetchStockOpnameDetail(
          stockOpnameId,
          pageDetailSO,
          rowsPerPageDetailSO
        );

        if (detail) {
          console.log("detail SO", detail);
          setDetailStockOpname(detail);

          if (detail.attachments && detail.attachments.length > 0) {
            const calculatedSummary = calculateSummary(detail.attachments);

            // Hitung Grand Total
            const grandTotal = calculateGrandTotal(calculatedSummary);

            // Update state dengan data yang dihitung
            setDetailStockOpnameInfo({
              ...detail.data[0],
              ...calculatedSummary,
              grandTotal: grandTotal,
            });
          } else {
            setDetailStockOpnameInfo(detail.data[0]);
          }

          setTotalRecordDetailSO(detail.totalPage * detail.limit || 0);
        } else {
          showError("Failed to load stock opname details");
        }
      } catch (error) {
        showError(error.message || "An error occurred while loading data");
      } finally {
        setIsLoading(false);
      }
    };

    loadDetailData();
  }, [id, pageDetailSO, rowsPerPageDetailSO]);

  // Update fungsi calculateSummary untuk menghitung dengan lebih akurat
  const calculateSummary = (items) => {
    if (!items || items.length === 0) {
      return {
        totalPlusQuantity: 0,
        totalMinusQuantity: 0,
        TotalPlusPrice: 0,
        TotalMinusPrice: 0,
      };
    }

    return items.reduce(
      (summary, item) => {
        const quantity = parseFloat(item.totalProductDiffrence || 0);
        const price = parseFloat(item.stockOpnamePrice || 0);

        if (item.stockOpnameTypeCode === "62") {
          // PLUS
          summary.totalPlusQuantity += quantity;
          summary.TotalPlusPrice += Math.abs(price); // Pastikan selalu positif
        } else if (item.stockOpnameTypeCode === "63") {
          // MINUS
          summary.totalMinusQuantity += Math.abs(quantity); // Pastikan selalu positif untuk tampilan
          summary.TotalMinusPrice += Math.abs(price); // Pastikan selalu positif
        }
        // Untuk type 61 (MATCH), tidak dihitung

        return summary;
      },
      {
        totalPlusQuantity: 0,
        totalMinusQuantity: 0,
        TotalPlusPrice: 0,
        TotalMinusPrice: 0,
      }
    );
  };

  // Fungsi untuk menghitung Grand Total
  const calculateGrandTotal = (summary) => {
    const plusAmount = summary?.TotalPlusPrice || 0;
    const minusAmount = summary?.TotalMinusPrice || 0;

    // Grand total = Plus Amount - Minus Amount
    return plusAmount - minusAmount;
  };

  // Handle pagination changes
  const handleChangePageDetailSO = (event, newPage) => {
    setPageDetailSO(newPage);
  };

  const handleChangeRowsPerPageDetailSO = (event) => {
    setRowsPerPageDetailSO(+event.target.value);
    setPageDetailSO(0);
  };

  // Utility functions
  const showError = (msg) => {
    setIsOpen(true);
    setAlertType("error");
    setMessage(msg);
  };

  // Get status color
  // Fungsi untuk mendapatkan status text berdasarkan status code
  const getStatusText = (status) => {
    switch (status) {
      case 200:
        return "Completed";
      case 100:
        return "Draft";
      default:
        return "-";
    }
  };

  // Fungsi untuk mendapatkan warna status berdasarkan status code atau status text
  const getStatusColor = (status, statusText) => {
    // Jika status code tersedia, gunakan untuk menentukan warna
    if (status) {
      switch (status) {
        case 200:
          return "success";
        case 100:
          return "warning";
        default:
          return "primary";
      }
    }

    // Fallback ke status text jika status code tidak tersedia
    switch (statusText) {
      case "Completed":
        return "success";
      case "Draft":
        return "warning";
      case "Cancelled":
        return "error";
      case "Pending":
        return "warning";
      default:
        return "primary";
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                color="inherit"
                onClick={() => navigate("/StockOpname")}
                sx={{cursor: "pointer", textDecoration: "none"}}
              >
                Stock Opname
              </Link>
              <Typography color="text.primary">Stock Opname Detail</Typography>
            </Breadcrumbs>

            <Typography variant="h6" sx={{mt: 1}}>
              Stock Opname #{detailStockOpnameInfo?.stockOpnameId || id}
            </Typography>
          </Box>
        </Box>

        {detailStockOpname ? (
          <>
            {/* Stock Opname Information */}
            <Card sx={{mb: 4}}>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Stock Opname Information
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Transaction ID
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailStockOpnameInfo?.stockOpnameId || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Date
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDate(detailStockOpnameInfo?.createdDate)}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Branch / Outlet
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {detailStockOpnameInfo?.branchName || "-"}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Chip
                          label={
                            stockOpnameFromState?.status
                              ? getStatusText(stockOpnameFromState.status)
                              : stockOpnameFromState?.statusName || "Completed"
                          }
                          color={getStatusColor(
                            stockOpnameFromState?.status,
                            stockOpnameFromState?.statusName
                          )}
                          size="small"
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Typography variant="h6" gutterBottom>
                      Summary
                    </Typography>

                    <Grid container spacing={1}>
                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Plus Items
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {NumberFormat(
                            0,
                            detailStockOpnameInfo?.totalPlusQuantity || 0
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Minus Items
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography variant="body2" fontWeight="medium">
                          {NumberFormat(
                            0,
                            detailStockOpnameInfo?.totalMinusQuantity || 0
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Plus Amount
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="success.main"
                        >
                          Rp.{" "}
                          {NumberFormat(
                            0,
                            detailStockOpnameInfo?.TotalPlusPrice || 0
                          )}
                        </Typography>
                      </Grid>

                      <Grid item xs={4}>
                        <Typography variant="body2" color="text.secondary">
                          Minus Amount
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <Typography
                          variant="body2"
                          fontWeight="medium"
                          color="error.main"
                        >
                          Rp.{" "}
                          {NumberFormat(
                            0,
                            detailStockOpnameInfo?.TotalMinusPrice || 0
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Products Table */}
            <Typography variant="h6" sx={{mb: 2}}>
              Product Items
            </Typography>

            <TableContainer>
              <Table aria-label="stock opname detail table">
                <TableHead>
                  <TableRow>
                    <TableCell>No</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell>Variant</TableCell>
                    <TableCell align="right">Stock Before</TableCell>
                    <TableCell align="right">Actual Stock</TableCell>
                    <TableCell align="right">Difference</TableCell>
                    <TableCell align="right">Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                    {/* <TableCell>Type</TableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!detailStockOpname.attachments ||
                  detailStockOpname.attachments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        No products available
                      </TableCell>
                    </TableRow>
                  ) : (
                    detailStockOpname.attachments.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{item.productName}</TableCell>
                        <TableCell>{item.variantName}</TableCell>
                        <TableCell align="right">
                          {NumberFormat(0, item.inventoryQuantity)}
                        </TableCell>
                        <TableCell align="right">
                          {NumberFormat(0, item.stockOpnameQuantity)}
                        </TableCell>
                        <TableCell align="right">
                          {NumberFormat(0, item.totalProductDiffrence)}
                        </TableCell>
                        <TableCell align="right">
                          Rp. {NumberFormat(0, item.trxPrice)}
                        </TableCell>
                        <TableCell align="right">
                          Rp. {NumberFormat(0, item.stockOpnamePrice)}
                        </TableCell>
                        {/* <TableCell>
                          <Chip
                            label={item.stockOpnameType}
                            color={
                              item.stockOpnameTypeCode === "62"
                                ? "success"
                                : "error"
                            }
                            size="small"
                          />
                        </TableCell> */}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {detailStockOpname.attachments &&
              detailStockOpname.attachments.length > 0 && (
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={totalRecordDetailSO}
                  rowsPerPage={rowsPerPageDetailSO}
                  page={pageDetailSO}
                  onPageChange={handleChangePageDetailSO}
                  onRowsPerPageChange={handleChangeRowsPerPageDetailSO}
                />
              )}

            {/* Total Section */}
            <Box sx={{mt: 3, display: "flex", justifyContent: "flex-end"}}>
              <Card sx={{minWidth: 300}}>
                <CardContent>
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">Plus Amount:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{textAlign: "right"}}>
                      <Typography variant="body2" color="success.main">
                        Rp.{" "}
                        {NumberFormat(
                          0,
                          detailStockOpnameInfo?.TotalPlusPrice || 0
                        )}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="body2">Minus Amount:</Typography>
                    </Grid>
                    <Grid item xs={6} sx={{textAlign: "right"}}>
                      <Typography variant="body2" color="error.main">
                        Rp.{" "}
                        {NumberFormat(
                          0,
                          detailStockOpnameInfo?.TotalMinusPrice || 0
                        )}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Divider sx={{my: 1}} />
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Grand Total:
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sx={{textAlign: "right"}}>
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        color={
                          detailStockOpnameInfo?.grandTotal >= 0
                            ? "success.main"
                            : "error.main"
                        }
                      >
                        Rp.{" "}
                        {NumberFormat(
                          0,
                          detailStockOpnameInfo?.grandTotal || 0
                        )}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            Stock opname details not available
          </Typography>
        )}

        {/* Action Buttons */}
        <Box sx={{mt: 4}}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/StockOpname")}
          >
            Back to Stock Opname
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default StockOpnameDetail;
